const MS_PER_DAY = 24 * 60 * 60 * 1000;

const toFiniteNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const parseDateParts = (value: unknown): Date | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().replace(/[./]/g, '-');
  const match = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!match) return undefined;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return undefined;

  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year
    || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day
  ) {
    return undefined;
  }

  return parsed;
};

const formatUtcDate = (value: Date): string => {
  const year = value.getUTCFullYear();
  const month = String(value.getUTCMonth() + 1).padStart(2, '0');
  const day = String(value.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addUtcDays = (value: Date, days: number): Date => new Date(value.getTime() + days * MS_PER_DAY);

const diffUtcDays = (left: Date, right: Date): number => Math.round((left.getTime() - right.getTime()) / MS_PER_DAY);

export function todayIsoDate(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDerivedCovariateFormulaName(item: {
  formula_name?: string | null;
  formula_version?: unknown;
  rule_key?: unknown;
  derived_from?: unknown;
  derived_formula?: unknown;
}): string | null {
  const formulaName = String(item.formula_name || '').trim();
  return formulaName || null;
}

export function buildPatientDerivedPatch(
  values: Record<string, unknown>,
  changedKeys: string[],
): Record<string, string | number> {
  const changed = new Set(changedKeys);
  const birthDate = parseDateParts(values.birth_date);
  const currentDate = parseDateParts(values.current_date);
  const ageYears = toFiniteNumber(values.age_years);

  if (changed.has('birth_date')) {
    if (!birthDate || !currentDate || currentDate < birthDate) return {};
    return {
      age_years: Number((diffUtcDays(currentDate, birthDate) / 365.25).toFixed(3)),
    };
  }

  if (changed.has('age_years')) {
    if (!currentDate || ageYears === undefined || ageYears < 0) return {};
    return {
      birth_date: formatUtcDate(addUtcDays(currentDate, -Math.round(ageYears * 365.25))),
    };
  }

  if (changed.has('current_date')) {
    if (birthDate && currentDate && currentDate >= birthDate) {
      return {
        age_years: Number((diffUtcDays(currentDate, birthDate) / 365.25).toFixed(3)),
      };
    }
    if (!currentDate || ageYears === undefined || ageYears < 0) return {};
    return {
      birth_date: formatUtcDate(addUtcDays(currentDate, -Math.round(ageYears * 365.25))),
    };
  }

  return {};
}
